// Iframe pricing module — 4 tiers (Mini / Magnum / Jeroboam / Enterprise).
// Mini/Magnum/Jeroboam = Stripe subscriptions with toggle monthly/yearly.
// Enterprise = contact-sales tier with custom quote.
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Check, Loader2, ArrowRight, Sparkles, Phone } from "lucide-react";
import { useT, useLang } from "@/lib/i18n";
import { apiFetch } from "@/lib/api";

const AUTH_KEY = "geovinum.auth.v1";

function authHeaders() {
  try {
    const raw = localStorage.getItem(AUTH_KEY);
    const parsed = raw ? JSON.parse(raw) : null;
    const t = parsed?.token;
    return t ? { Authorization: `Bearer ${t}` } : {};
  } catch {
    return {};
  }
}

const fmtEUR = (cents) =>
  cents != null
    ? new Intl.NumberFormat("fr-FR", { style: "currency", currency: "EUR", maximumFractionDigits: 0 }).format(cents / 100)
    : "—";

const IframePricing = () => {
  const t = useT();
  const lang = useLang();
  const [data, setData] = useState(null);
  const [cycle, setCycle] = useState("yearly"); // default to annual (highlights savings)
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    (async () => {
      const { ok, data, error: err } = await apiFetch(`/api/iframe/plans`);
      if (!ok) setError(err);
      else setData(data);
      setLoading(false);
    })();
  }, []);

  const buy = async (tierId) => {
    setSubmitting(tierId);
    setError(null);
    const { ok, data, error: err } = await apiFetch(`/api/iframe/checkout`, {
      method: "POST",
      headers: authHeaders(),
      body: JSON.stringify({
        tier_id: tierId,
        cycle,
        origin_url: window.location.origin,
        lang: lang || "fr",
      }),
    });
    if (!ok) {
      setError(err);
      setSubmitting(null);
      return;
    }
    window.location.href = data.url;
  };

  if (loading) {
    return (
      <section className="py-16 bg-[color:var(--gv-mist)]">
        <div className="max-w-6xl mx-auto px-6 lg:px-10 text-center">
          <Loader2 size={20} className="animate-spin mx-auto text-[color:var(--gv-pink)]" />
        </div>
      </section>
    );
  }
  if (error && !data) {
    return (
      <section className="py-16 bg-[color:var(--gv-mist)]">
        <div className="max-w-3xl mx-auto px-6 lg:px-10">
          <div className="bg-red-50 border border-red-200 text-red-700 text-[13px] px-4 py-3 rounded-[5px]">{error}</div>
        </div>
      </section>
    );
  }

  return (
    <section className="py-16 md:py-24 bg-[color:var(--gv-mist)]" data-testid="iframe-pricing">
      <div className="max-w-6xl mx-auto px-6 lg:px-10">
        <div className="text-center mb-10">
          <div className="inline-flex items-center gap-2 bg-white border border-[color:var(--gv-line)] text-[color:var(--gv-blue)] px-3 py-1.5 rounded-full text-[10.5px] uppercase tracking-[0.15em] font-medium mb-4 shadow-sm">
            <Sparkles size={11} /> {t("iframeP.eyebrow")}
          </div>
          <h2 className="font-display text-3xl md:text-5xl text-[color:var(--gv-ink)] leading-[1.1] mb-3">
            {t("iframeP.title")}
          </h2>

          {/* Cycle toggle */}
          <div className="inline-flex items-center bg-white border border-[color:var(--gv-line)] rounded-full p-1 mt-6">
            <button
              onClick={() => setCycle("monthly")}
              aria-pressed={cycle === "monthly"}
              className={`px-4 py-1.5 rounded-full text-[13px] transition-colors ${
                cycle === "monthly"
                  ? "bg-[color:var(--gv-ink)] text-white"
                  : "text-[color:var(--gv-ink)]/65 hover:text-[color:var(--gv-ink)]"
              }`}
              data-testid="iframe-cycle-monthly"
            >
              {t("iframeP.cycle.monthly")}
            </button>
            <button
              onClick={() => setCycle("yearly")}
              aria-pressed={cycle === "yearly"}
              className={`px-4 py-1.5 rounded-full text-[13px] transition-colors inline-flex items-center gap-1.5 ${
                cycle === "yearly"
                  ? "bg-[color:var(--gv-ink)] text-white"
                  : "text-[color:var(--gv-ink)]/65 hover:text-[color:var(--gv-ink)]"
              }`}
              data-testid="iframe-cycle-yearly"
            >
              {t("iframeP.cycle.yearly")}
              <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${cycle === "yearly" ? "bg-[color:var(--gv-pink)] text-white" : "bg-[color:var(--gv-pink)]/10 text-[color:var(--gv-pink)]"}`}>
                –17%
              </span>
            </button>
          </div>
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 text-[13px] px-4 py-3 rounded-[5px] mb-4 max-w-md mx-auto">{error}</div>
        )}

        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-5">
          {(data?.tiers || []).map((tier) => {
            // Enterprise / contact-only tier: special card with no Stripe price
            if (tier.contact_only) {
              return (
                <div
                  key={tier.id}
                  className="bg-[color:var(--gv-ink)] text-white rounded-[10px] p-6 md:p-7 border-2 border-[color:var(--gv-ink)] flex flex-col shadow-lg"
                  data-testid={`iframe-tier-${tier.id}`}
                >
                  <div className="self-start inline-flex items-center gap-1 bg-white/15 text-white text-[10px] uppercase tracking-widest font-medium px-2 py-0.5 rounded-full mb-3">
                    <Phone size={10} /> {t("iframeP.contact_badge")}
                  </div>
                  <div className="font-display text-2xl mb-1">{tier.name}</div>
                  <p className="text-[13px] text-white/70 leading-relaxed mb-5 min-h-[3em]">{tier.tagline}</p>
                  <div className="mb-5">
                    <div className="flex items-baseline gap-1.5">
                      <span className="font-display text-4xl">
                        {tier.price_per_request_eur != null
                          ? new Intl.NumberFormat("fr-FR", { style: "currency", currency: "EUR", maximumFractionDigits: 2 }).format(tier.price_per_request_eur)
                          : "—"}
                      </span>
                      <span className="text-[13px] text-white/55">/ {t("iframeP.request")}</span>
                    </div>
                    <div className="text-[12px] text-white/55 mt-1">{t("iframeP.from")} · {t("iframeP.custom_quote")}</div>
                  </div>
                  <ul className="space-y-2.5 mb-7 flex-1">
                    {tier.features.map((f) => (
                      <li key={f} className="flex items-start gap-2 text-[13.5px] text-white/80">
                        <Check size={14} className="text-[color:var(--gv-pink)] mt-0.5 shrink-0" />
                        <span>{f}</span>
                      </li>
                    ))}
                  </ul>
                  <Link
                    to={tier.contact_url || "/contact?topic=iframe-enterprise"}
                    className="inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-[5px] bg-[color:var(--gv-pink)] text-white text-[13.5px] font-medium hover:bg-[color:var(--gv-pink-dark)] transition-colors"
                    data-testid={`iframe-contact-${tier.id}`}
                  >
                    {t("iframeP.talk_to_sales")} <ArrowRight size={14} />
                  </Link>
                </div>
              );
            }

            const price = tier[cycle];
            const otherPrice = cycle === "yearly" ? tier.monthly : tier.yearly;
            const monthlyEquivalent = cycle === "yearly" && price ? Math.round(price.unit_amount_cents / 12) : null;
            return (
              <div
                key={tier.id}
                className={`bg-white rounded-[10px] p-6 md:p-7 border-2 transition-all flex flex-col ${
                  tier.popular ? "border-[color:var(--gv-pink)] shadow-lg" : "border-[color:var(--gv-line)]"
                }`}
                data-testid={`iframe-tier-${tier.id}`}
              >
                {tier.popular && (
                  <div className="self-start inline-flex items-center gap-1 bg-[color:var(--gv-pink)] text-white text-[10px] uppercase tracking-widest font-medium px-2 py-0.5 rounded-full mb-3">
                    {t("iframeP.popular")}
                  </div>
                )}
                <div className="font-display text-2xl text-[color:var(--gv-ink)] mb-1">{tier.name}</div>
                <p className="text-[13px] text-[color:var(--gv-ink)]/65 leading-relaxed mb-5 min-h-[3em]">{tier.tagline}</p>

                <div className="mb-5">
                  {price ? (
                    <>
                      <div className="flex items-baseline gap-1.5">
                        <span className="font-display text-4xl text-[color:var(--gv-ink)]">{fmtEUR(price.unit_amount_cents)}</span>
                        <span className="text-[13px] text-[color:var(--gv-ink)]/55">
                          {t("iframeP.ht_per")} {price.interval === "year" ? t("iframeP.year") : t("iframeP.month")}
                        </span>
                      </div>
                      {tier.price_per_request_eur != null && (
                        <div className="text-[11.5px] text-[color:var(--gv-blue)] mt-1.5 font-medium">
                          {new Intl.NumberFormat("fr-FR", { style: "currency", currency: "EUR", maximumFractionDigits: 2 }).format(tier.price_per_request_eur)} / {t("iframeP.request")}
                          {tier.quota_per_year != null && (
                            <span className="text-[color:var(--gv-ink)]/55 font-normal"> · {tier.quota_per_year.toLocaleString("fr-FR")} {t("iframeP.requests_per_year")}</span>
                          )}
                        </div>
                      )}
                      {monthlyEquivalent && (
                        <div className="text-[12px] text-[color:var(--gv-ink)]/55 mt-1">
                          ≈ {fmtEUR(monthlyEquivalent)} / {t("iframeP.month")}
                        </div>
                      )}
                      {otherPrice && cycle === "monthly" && (
                        <div className="text-[11.5px] text-[color:var(--gv-pink)] mt-1">
                          {t("iframeP.save_with_yearly", { amount: fmtEUR(otherPrice.unit_amount_cents) })}
                        </div>
                      )}
                    </>
                  ) : (
                    <div className="text-[13px] text-[color:var(--gv-ink)]/40">{t("iframeP.unavailable")}</div>
                  )}
                </div>

                <ul className="space-y-2.5 mb-7 flex-1">
                  {tier.features.map((f) => (
                    <li key={f} className="flex items-start gap-2 text-[13.5px] text-[color:var(--gv-ink)]/75">
                      <Check size={14} className="text-[color:var(--gv-pink)] mt-0.5 shrink-0" />
                      <span>{f}</span>
                    </li>
                  ))}
                </ul>

                <button
                  onClick={() => buy(tier.id)}
                  disabled={!price || submitting === tier.id}
                  className={tier.popular ? "btn-primary justify-center" : "btn-ghost justify-center"}
                  data-testid={`iframe-buy-${tier.id}`}
                >
                  {submitting === tier.id ? (
                    <Loader2 size={14} className="animate-spin" />
                  ) : (
                    <>{t("iframeP.subscribe")} <ArrowRight size={14} /></>
                  )}
                </button>
              </div>
            );
          })}
        </div>

        <p className="text-center text-[11.5px] text-[color:var(--gv-ink)]/45 mt-8">
          {t("iframeP.footer_note")}
        </p>
      </div>
    </section>
  );
};

export default IframePricing;
